import { useEffect, useState } from 'react';
import { ThemeProps } from '../utils/theme';
import { getClarityIdByName } from '../services/clarityService';

interface ClarityAnalyticsProps {
  theme?: ThemeProps;
}

declare global {
  interface Window {
    clarity: any;
  }
}

const ClarityAnalytics: React.FC<ClarityAnalyticsProps> = ({ theme }) => {
  const [clarityId, setClarityId] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    const fetchClarityId = async () => {
      let finalClarityId = 'rqloengqf3'; // Default fallback
      
      if (theme?.name) {
        try {
          finalClarityId = await getClarityIdByName(theme.name);
        } catch (error) {
          console.error('Failed to fetch Clarity ID, using fallback');
        }
      }
      
      setClarityId(finalClarityId);
    };

    fetchClarityId();
  }, [theme?.name]);

  useEffect(() => {
    // Don't initialize if we don't have a clarity ID or already initialized
    if (!clarityId || isInitialized) {
      return;
    }

    // Check if Clarity script is already loaded
    const existingScript = document.querySelector(`script[src*="clarity.ms/tag/${clarityId}"]`);
    if (existingScript) {
      setIsInitialized(true);
      
      // Still set the theme name even if script exists
      if (theme?.name && window.clarity) {
        window.clarity('set', 'siteName', theme.name);
      }
      return;
    }

    // Inject Clarity tracking script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${clarityId}`;
    script.onload = () => {
      setIsInitialized(true);
      
      // Set custom tags after script loads
      if (theme?.name) {
        setTimeout(() => {
          if (window.clarity) {
            // Set primary site identifier
            window.clarity('set', 'siteName', theme.name);
            
            // Optional: Set additional useful data
            if (theme.website) {
              window.clarity('set', 'websiteUrl', theme.website);
            }
            
            // Set environment info
            window.clarity('set', 'environment', window.location.hostname);
          }
        }, 500);
      }
    };
    script.onerror = () => {
      console.error('Failed to load Clarity script');
    };
    
    document.head.appendChild(script);

    // Define Clarity function immediately
    window.clarity = window.clarity || function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

  }, [clarityId, theme?.name, theme?.website, isInitialized]);

  return null;
};

export default ClarityAnalytics;